import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  CircularProgress,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { displayPatientNameFromParts } from "../../utils/displayPatientName";
import { patientAgeYears } from "../../context/normalizePatient";
import { fetchPatientRoster, isAbortError } from "./api";
import {
  PR_COLORS,
  ROSTER_PAGE_SIZE,
  ROSTER_SEARCH_DEBOUNCE_MS,
} from "./constants";
import { formatShortDate } from "./formatters";

/** Age and sex together — this pairing is what separates two same-name Patients. */
const formatAgeSex = (patient) => {
  const years = patientAgeYears(patient);
  const sex = (patient.gender || "").charAt(0).toUpperCase();
  if (years === "" && !sex) return "—";
  if (years === "") return sex;
  return sex ? `${years} / ${sex}` : String(years);
};

const initialsOf = (patient) => {
  const name = displayPatientNameFromParts(patient, "");
  if (!name) return "?";
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2);
  return `${parts[0][0]}${parts[parts.length - 1][0]}`;
};

const HEAD_CELL_SX = {
  fontWeight: 700,
  color: PR_COLORS.heading,
  backgroundColor: PR_COLORS.tableHead,
  whiteSpace: "nowrap",
};

/**
 * The Facility's Patient roster — one Patient per line, paged, searchable.
 *
 * Page and committed search term are owned by the shell, so returning from a
 * Patient Overview lands the user back where they were rather than resetting to
 * page one with a cleared search.
 */
export default function PatientListPane({
  page,
  search,
  onPageChange,
  onSearchChange,
  onSelectPatient,
}) {
  const [searchInput, setSearchInput] = useState(search || "");
  const [patients, setPatients] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const debounceRef = useRef(null);
  const abortRef = useRef(null);

  const loadRoster = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError("");
    fetchPatientRoster({
      page: page + 1, // TablePagination is 0-based, the API is 1-based
      pageSize: ROSTER_PAGE_SIZE,
      search,
      signal: controller.signal,
    })
      .then(({ patients: rows, totalCount: total }) => {
        if (controller.signal.aborted) return;
        setPatients(rows);
        setTotalCount(total);
      })
      .catch((err) => {
        if (isAbortError(err)) return;
        console.error("Failed to load patient roster:", err);
        setPatients([]);
        setTotalCount(0);
        setError("Could not load the patient list. Please try again.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
  }, [page, search]);

  useEffect(() => {
    loadRoster();
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [loadRoster]);

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  // Debounce so the roster does not thrash mid-word. Whatever is typed is
  // committed as-is — a single character is a legitimate search, and an empty
  // box restores the full roster.
  const handleSearchInput = (event) => {
    const value = event.target.value;
    setSearchInput(value);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onSearchChange(value.trim());
    }, ROSTER_SEARCH_DEBOUNCE_MS);
  };

  const isSearching = Boolean(search);
  const showEmpty = !loading && !error && patients.length === 0;

  return (
    <Box>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: "12px",
          border: `1px solid ${PR_COLORS.border}`,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: PR_COLORS.heading,
            fontWeight: 700,
            mb: 1.5,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <GroupOutlinedIcon /> Patient List
          {totalCount > 0 && (
            <Typography
              component="span"
              sx={{ color: PR_COLORS.muted, fontSize: "0.9rem", fontWeight: 500 }}
            >
              ({totalCount} {totalCount === 1 ? "patient" : "patients"})
            </Typography>
          )}
        </Typography>

        <TextField
          fullWidth
          value={searchInput}
          onChange={handleSearchInput}
          placeholder="Search by name, patient ID, phone or Aadhaar number..."
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: PR_COLORS.muted }} />
              </InputAdornment>
            ),
            endAdornment: loading ? (
              <InputAdornment position="end">
                <CircularProgress size={18} />
              </InputAdornment>
            ) : null,
          }}
          sx={{ backgroundColor: "#fff" }}
        />
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          borderRadius: "12px",
          border: `1px solid ${PR_COLORS.border}`,
          overflow: "hidden",
        }}
      >
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...HEAD_CELL_SX, width: 56 }} />
                <TableCell sx={HEAD_CELL_SX}>Name</TableCell>
                <TableCell sx={HEAD_CELL_SX}>Patient ID</TableCell>
                <TableCell sx={HEAD_CELL_SX}>Age / Sex</TableCell>
                <TableCell sx={HEAD_CELL_SX}>Phone</TableCell>
                <TableCell sx={HEAD_CELL_SX}>Registered</TableCell>
                <TableCell sx={{ ...HEAD_CELL_SX, width: 44 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {patients.map((patient) => (
                <TableRow
                  key={patient.patient_id}
                  hover
                  tabIndex={0}
                  role="button"
                  aria-label={`Open ${displayPatientNameFromParts(
                    patient,
                    "patient",
                  )}`}
                  onClick={() => onSelectPatient(patient)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      onSelectPatient(patient);
                    }
                  }}
                  sx={{
                    cursor: "pointer",
                    "&:hover": { backgroundColor: PR_COLORS.menuIdle },
                    "&:focus-visible": {
                      outline: `2px solid ${PR_COLORS.primary}`,
                      outlineOffset: "-2px",
                    },
                    "&:hover .pr-row-chevron": { color: PR_COLORS.primary },
                  }}
                >
                  <TableCell>
                    <Avatar
                      sx={{
                        bgcolor: PR_COLORS.heading,
                        width: 34,
                        height: 34,
                        fontSize: "0.8rem",
                      }}
                    >
                      {initialsOf(patient)}
                    </Avatar>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontWeight: 600, fontSize: "0.9rem" }}>
                      {displayPatientNameFromParts(patient, "UNKNOWN")}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {patient.patient_id || "—"}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {formatAgeSex(patient)}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {patient.phone_no || "—"}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {formatShortDate(patient.registration_datetime)}
                  </TableCell>
                  {/* Visible at rest, not only on hover — this is what makes the
                      row read as a link before the user interacts with it. */}
                  <TableCell sx={{ width: 44, textAlign: "right" }}>
                    <ChevronRightIcon
                      className="pr-row-chevron"
                      fontSize="small"
                      sx={{ color: PR_COLORS.chevron }}
                    />
                  </TableCell>
                </TableRow>
              ))}

              {loading && patients.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} sx={{ py: 6, textAlign: "center" }}>
                    <CircularProgress size={28} />
                    <Typography sx={{ mt: 1.5, color: PR_COLORS.muted }}>
                      Loading patients…
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {showEmpty && (
                <TableRow>
                  <TableCell colSpan={7} sx={{ py: 6, textAlign: "center" }}>
                    <PersonSearchIcon
                      sx={{ fontSize: 56, color: PR_COLORS.placeholder, mb: 1 }}
                    />
                    <Typography sx={{ color: PR_COLORS.quiet, fontWeight: 500 }}>
                      {isSearching
                        ? "No patients match your search"
                        : "No patients registered at this facility yet"}
                    </Typography>
                    <Typography
                      sx={{ color: PR_COLORS.faint, fontSize: "0.85rem", mt: 0.5 }}
                    >
                      {isSearching
                        ? "Check the spelling, or register this patient from Patient Management."
                        : "Register your first patient from Patient Management."}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {totalCount > 0 && (
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(event, nextPage) => onPageChange(nextPage)}
            rowsPerPage={ROSTER_PAGE_SIZE}
            rowsPerPageOptions={[ROSTER_PAGE_SIZE]}
            sx={{ borderTop: `1px solid ${PR_COLORS.border}` }}
          />
        )}
      </Paper>
    </Box>
  );
}
